import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CommonTable from '../../../common_components/CommonTable';
import Pagination from '../../../common_components/Pagination';
import CommonTabs from '../../../common_components/CommonTabs';
import TableToolbar from '../../../common_components/TableToolbar';
import HoverTooltip from '../../../common_components/hoverOverlay/HoverTooltip';
import { NETWORK_METADATA_MAP } from '../../../utils/networkConstants';
import { useDispatch, useSelector } from 'react-redux';
import { InlineTagDropdown } from '../../PublisherTagging/components/InlineTagDropdown';
import { updatePublisherTag } from '../../../redux/publishertaggingslice';
import InfoTooltip from '../../../common_components/InfoTooltip';
import { StyledCurrency } from '../../../helper/StyledCurrency';
import { useAccountOverview } from '../../../context/AccountOverviewContext';
import ReferralUrlsModal from './ReferralUrlsModal';
import { selectEntityType } from '../../../redux/authSlice';
// All-tab column id → backend sort key / row field when the Orders view is on.
const ORDERS_SORT_KEY = {
    revenue: 'orders', tof: 'tof_orders', mof: 'mof_orders', bof: 'bof_orders',
};
const ORDERS_SORT_KEY_REVERSE = Object.fromEntries(Object.entries(ORDERS_SORT_KEY).map(([k, v]) => [v, k]));
// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtNum = (v) => (v ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtCurr = (v) => (v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDec = (v) => (v ?? 0).toFixed(2);
// Some publisher names arrive as raw URLs (e.g. awin's feed stores the site URL
// as the publisher name). Render those as a readable brand-ish label instead.
// Only values that are clearly URLs are rewritten — real names that merely
// contain a dot (e.g. "Demand.io") are left untouched.
const cleanPartnerName = (raw) => {
    const name = (raw || '').trim();
    if (!/^(https?:\/\/|www\.)/i.test(name))
        return name;
    const host = name
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .split('/')[0]
        .split('?')[0];
    const label = host.split('.')[0] || host;
    if (!label)
        return name;
    // Preserve intentional casing (e.g. "BradsDeals"); otherwise capitalize.
    return /[A-Z]/.test(label) ? label : label.charAt(0).toUpperCase() + label.slice(1);
};
const formatVal = (v, format) => {
    if (format === 'currency')
        return fmtCurr(v);
    if (format === 'decimal')
        return fmtDec(v);
    return fmtNum(v);
};
// ─── Compare metric cell ──────────────────────────────────────────────────────
const MetricCell = ({ metric, format, prefix = '', isInverse, contributionPct, hasDottedUnderline = false }) => {
    const hasCompare = metric?.compare !== undefined;
    const pct = metric?.percentage_difference;
    const isUp = (pct ?? 0) >= 0;
    const good = isInverse ? !isUp : isUp;
    const chipColor = !hasCompare ? 'transparent' : good ? '#ECFDF3' : '#FEF3F2';
    const textColor = !hasCompare ? '#101828' : good ? '#027A48' : '#B42318';
    const arrow = isUp ? '/assets/trend-up.svg' : '/assets/trending-down.svg';
    const badge = hasCompare && (_jsxs(Box, { className: "publisher-pct-badge", sx: {
            display: 'flex', alignItems: 'center', gap: '2px',
            px: '6px', py: '1px', borderRadius: '8px',
            bgcolor: chipColor, color: textColor,
            fontSize: '13px', fontWeight: 500,
        }, children: [_jsx(StyledCurrency, { value: `${Math.abs(pct ?? 0).toFixed(0)}%`, symbolWeight: 500, valueWeight: 500, fontSize: "13px", symbolSize: "13px", symbolColor: textColor, valueColor: textColor }), _jsx("img", { src: arrow, alt: "", width: 10, height: 10, onError: (e) => { e.target.style.display = 'none'; } })] }));
    const currentValueEl = (_jsx(Typography, { className: "all-Font-Sizes", sx: {
            fontSize: '14px !important',
            color: '#101828',
            fontWeight: 400,
            display: 'inline-flex',
            alignItems: 'center',
            borderBottom: hasDottedUnderline ? '2px dotted #181d27' : 'none',
            pb: hasDottedUnderline ? '1px' : '0px',
            width: 'fit-content',
        }, children: prefix ? (_jsx(StyledCurrency, { value: `${prefix}${formatVal(metric?.current ?? 0, format)}`, symbolWeight: 400, valueWeight: 400, fontSize: "14px", symbolSize: "14px", symbolColor: "#98A2B3", valueColor: "#101828" })) : (formatVal(metric?.current ?? 0, format)) }));
    const compareValueEl = hasCompare && (_jsx(Typography, { className: "all-Font-Sizes", sx: {
            color: '#667085',
            fontSize: '14px !important',
            fontWeight: 400,
            display: 'inline-flex',
            alignItems: 'center',
            borderBottom: hasDottedUnderline ? '2px dotted #71717a' : 'none',
            pb: hasDottedUnderline ? '1px' : '0px',
            width: 'fit-content',
        }, children: prefix ? (_jsx(StyledCurrency, { value: `${prefix}${formatVal(metric.compare, format)}`, symbolWeight: 400, valueWeight: 400, fontSize: "14px", symbolSize: "14px", symbolColor: "#667085", valueColor: "#667085" })) : (formatVal(metric.compare, format)) }));
    // Columns with a contribution % (New/Engaged/Existing Customer, "All" tab only) use
    // a row-based layout: current+compare on row 1, contribution%+badge on row 2.
    // Every other column keeps the original stacked layout (value/compare left,
    // badge/contribution right) unchanged.
    if (contributionPct !== undefined) {
        // A missing prior-year value here means "no data", not "hide the row" — default
        // to 0 so the compare value + badge always render and every row stays aligned.
        const compareForced = metric?.compare ?? 0;
        const pctForced = metric?.percentage_difference ?? 0;
        const isUpForced = pctForced >= 0;
        const goodForced = isInverse ? !isUpForced : isUpForced;
        const chipColorForced = goodForced ? '#ECFDF3' : '#FEF3F2';
        const textColorForced = goodForced ? '#027A48' : '#B42318';
        const arrowForced = isUpForced ? '/assets/trend-up.svg' : '/assets/trending-down.svg';
        const compareValueElForced = (_jsx(Typography, { className: "all-Font-Sizes", sx: { color: '#667085', fontSize: '14px !important', fontWeight: 400, display: 'flex', alignItems: 'center' }, children: prefix ? (_jsx(StyledCurrency, { value: `${prefix}${formatVal(compareForced, format)}`, symbolWeight: 400, valueWeight: 400, fontSize: "14px", symbolSize: "14px", symbolColor: "#667085", valueColor: "#667085" })) : (formatVal(compareForced, format)) }));
        const badgeForced = (_jsxs(Box, { className: "publisher-pct-badge", sx: {
                display: 'flex', alignItems: 'center', gap: '2px',
                px: '6px', py: '1px', borderRadius: '8px',
                bgcolor: chipColorForced, color: textColorForced,
                fontSize: '13px', fontWeight: 500,
            }, children: [_jsx(StyledCurrency, { value: `${Math.abs(pctForced).toFixed(0)}%`, symbolWeight: 500, valueWeight: 500, fontSize: "13px", symbolSize: "13px", symbolColor: textColorForced, valueColor: textColorForced }), _jsx("img", { src: arrowForced, alt: "", width: 10, height: 10, onError: (e) => { e.target.style.display = 'none'; } })] }));
        return (_jsxs(Box, { sx: { display: 'flex', gap: '8px', alignItems: 'center' }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '2px' }, children: [currentValueEl, compareValueElForced] }), badgeForced] }));
    }
    return (_jsxs(Box, { sx: { display: 'flex', gap: '6px' }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '2px' }, children: [currentValueEl, compareValueEl] }), hasCompare && (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start', justifyContent: 'center' }, children: badge }))] }));
};
// ─── Summary row ──────────────────────────────────────────────────────────────
// Mirrors the backend's calculatePercentageDiff so the Totals row's comparison
// badge matches the per-row badges exactly.
const pctDiff = (current, previous) => {
    if (previous === 0)
        return current === 0 ? 0 : 100;
    return ((current - previous) / Math.abs(previous)) * 100;
};
const safeDiv = (n, d) => (d > 0 ? n / d : 0);
// Totals-row cell: bold current value plus, when compare mode is on, the
// comparison-period value below and a percentage-difference badge — matching the
// per-row MetricCell so the footer reads the same way as the rows above it.
const SummaryMetricCell = ({ current, compare, format, prefix = '', hasCompare, isInverse, contributionPct }) => {
    const pct = pctDiff(current, compare);
    const isUp = pct >= 0;
    const good = isInverse ? !isUp : isUp;
    const chipColor = good ? '#ECFDF3' : '#FEF3F2';
    const textColor = good ? '#027A48' : '#B42318';
    const arrow = isUp ? '/assets/trend-up.svg' : '/assets/trending-down.svg';
    const badge = hasCompare && (_jsxs(Box, { className: "publisher-pct-badge", sx: {
            display: 'flex', alignItems: 'center', gap: '2px',
            px: '6px', py: '1px', borderRadius: '8px',
            bgcolor: chipColor, color: textColor,
            fontSize: '12px', fontWeight: 600,
        }, children: [_jsx(StyledCurrency, { value: `${Math.abs(pct).toFixed(0)}%`, symbolWeight: 600, valueWeight: 600, fontSize: "12px", symbolSize: "12px", symbolColor: textColor, valueColor: textColor }), _jsx("img", { src: arrow, alt: "", width: 10, height: 10, onError: (e) => { e.target.style.display = 'none'; } })] }));
    const currentValueEl = (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#101828', fontWeight: 700, display: 'flex', alignItems: 'center' }, children: prefix ? (_jsx(StyledCurrency, { value: `${prefix}${formatVal(current, format)}`, symbolWeight: 400, valueWeight: 700, fontSize: "14px", symbolSize: "14px", symbolColor: "#98A2B3", valueColor: "#101828" })) : (formatVal(current, format)) }));
    const compareValueEl = hasCompare && (_jsx(Typography, { className: "all-Font-Sizes", sx: { color: '#667085', fontSize: '14px !important', fontWeight: 400, display: 'flex', alignItems: 'center' }, children: prefix ? (_jsx(StyledCurrency, { value: `${prefix}${formatVal(compare, format)}`, symbolWeight: 400, valueWeight: 400, fontSize: "14px", symbolSize: "14px", symbolColor: "#667085", valueColor: "#667085" })) : (formatVal(compare, format)) }));
    // Columns with a contribution % (New/Engaged/Existing Customer, "All" tab only) use
    // a row-based layout: current+compare on row 1, contribution%+badge on row 2.
    // Every other column keeps the original stacked layout unchanged.
    if (contributionPct !== undefined) {
        return (_jsxs(Box, { sx: { display: 'flex', gap: '8px', alignItems: 'center' }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '2px' }, children: [currentValueEl, compareValueEl] }), badge] }));
    }
    return (_jsxs(Box, { sx: { display: 'flex', gap: '6px' }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '2px' }, children: [currentValueEl, compareValueEl] }), hasCompare && (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start', justifyContent: 'center' }, children: badge }))] }));
};
const buildOrdersSummaryRenderer = (metrics, hasCompare) => (_rows, column) => {
    const total = metrics.total_purchase;
    const pct = (n) => (total > 0 ? (n / total) * 100 : 0);
    const cell = (current, compare, withPct) => (_jsx(SummaryMetricCell, { current: current, compare: compare, format: "number", hasCompare: hasCompare, contributionPct: withPct ? pct(current) : undefined }));
    switch (column.id) {
        case 'partner_name':
            return _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', fontWeight: 700, color: '#344054' }, children: "Totals" });
        case 'revenue': return cell(total, metrics.total_purchase_compare ?? 0, false);
        case 'tof': return cell(metrics.tof_orders ?? 0, metrics.tof_orders_compare ?? 0, true);
        case 'mof': return cell(metrics.mof_orders ?? 0, metrics.mof_orders_compare ?? 0, true);
        case 'bof': return cell(metrics.bof_orders ?? 0, metrics.bof_orders_compare ?? 0, true);
        default: return null;
    }
};
const buildSummaryRenderer = (metrics, hasCompare) => (_rows, column) => {
    // ROAS totals are the aggregate revenue ÷ spend (not a sum of per-row ROAS),
    // for both the current and compare periods.
    const roas = safeDiv(metrics.revenue, metrics.spend);
    const roasCompare = safeDiv(metrics.revenue_compare ?? 0, metrics.spend_compare ?? 0);
    switch (column.id) {
        case 'partner_name':
            return _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', fontWeight: 700, color: '#344054' }, children: "Totals" });
        case 'revenue':
            return _jsx(SummaryMetricCell, { current: metrics.revenue, compare: metrics.revenue_compare ?? 0, format: "currency", prefix: "$", hasCompare: hasCompare });
        case 'roas':
            return _jsx(SummaryMetricCell, { current: roas, compare: roasCompare, format: "decimal", hasCompare: hasCompare });
        case 'clicks':
            return _jsx(SummaryMetricCell, { current: metrics.clicks, compare: metrics.clicks_compare ?? 0, format: "number", hasCompare: hasCompare });
        case 'orders':
            return _jsx(SummaryMetricCell, { current: metrics.total_purchase, compare: metrics.total_purchase_compare ?? 0, format: "number", hasCompare: hasCompare });
        case 'spend':
            return _jsx(SummaryMetricCell, { current: metrics.spend, compare: metrics.spend_compare ?? 0, format: "currency", prefix: "$", hasCompare: hasCompare });
        case 'tof':
            return _jsx(SummaryMetricCell, { current: metrics.tof ?? 0, compare: metrics.tof_compare ?? 0, format: "currency", prefix: "$", hasCompare: hasCompare, contributionPct: metrics.revenue > 0 ? ((metrics.tof ?? 0) / metrics.revenue) * 100 : 0 });
        case 'mof':
            return _jsx(SummaryMetricCell, { current: metrics.mof ?? 0, compare: metrics.mof_compare ?? 0, format: "currency", prefix: "$", hasCompare: hasCompare, contributionPct: metrics.revenue > 0 ? ((metrics.mof ?? 0) / metrics.revenue) * 100 : 0 });
        case 'bof':
            return _jsx(SummaryMetricCell, { current: metrics.bof ?? 0, compare: metrics.bof_compare ?? 0, format: "currency", prefix: "$", hasCompare: hasCompare, contributionPct: metrics.revenue > 0 ? ((metrics.bof ?? 0) / metrics.revenue) * 100 : 0 });
        default:
            return null;
    }
};
// ─── Question circle icon (matches platform style) ────────────────────────────
const PublisherTable = ({ tabs, activeTabKey, onTabChange, onPageChange, onSortChange, sortBy, sortOrder, loading = false, onRefresh, hasCompare = false, }) => {
    const dispatch = useDispatch();
    const permissions = useSelector((state) => state.auth.permissions);
    const updating = useSelector((state) => state.publisherTagging.updating);
    const entityType = useSelector(selectEntityType);
    const isClient = entityType === 'client';
    const canManageTagging = useMemo(() => permissions?.["publisher-tagging"]?.actions?.includes("Manage Tagging"), [permissions]);
    const handleSaveTag = useCallback(async (rowId, newTag) => {
        try {
            await dispatch(updatePublisherTag({
                publisher_id: rowId,
                tag: newTag
            })).unwrap();
            if (onRefresh)
                onRefresh();
        }
        catch (err) {
            console.error('Failed to update tag:', err);
        }
    }, [dispatch, onRefresh]);
    const { selectedClientId, dateRange } = useAccountOverview();
    const [referralModalOpen, setReferralModalOpen] = useState(false);
    const [selectedPublisherForReferrals, setSelectedPublisherForReferrals] = useState(null);
    const handleOrdersClick = useCallback((row) => {
        const net = (row.network || '').toLowerCase().trim();
        if (net === 'levanta' || net === 'awin' || net === 'refersion' || net === 'costingtons' || net === 'wayward') {
            return;
        }
        setSelectedPublisherForReferrals({
            id: row.publisher_id,
            name: cleanPartnerName(row.partner_name),
            network: row.network,
        });
        setReferralModalOpen(true);
    }, []);
    const [searchVal, setSearchVal] = useState('');
    const [allView, setAllView] = useState('revenue');
    // Revenue/Orders toggle only applies to the All tab.
    const isOrdersView = allView === 'orders' && activeTabKey === 'all';
    const toSortKey = useCallback((colId) => (isOrdersView ? ORDERS_SORT_KEY[colId] ?? colId : colId), [isOrdersView]);
    // View-only switch: the backend already returns both revenue and order figures,
    // so toggling must not touch sort state (that would refetch the whole page).
    const handleViewChange = useCallback((view) => setAllView(view), []);
    const sortedTabs = useMemo(() => {
        let currentTabs = tabs;
        if (isClient) {
            currentTabs = tabs.filter(t => {
                const key = t.key?.toLowerCase();
                const label = t.label?.toLowerCase();
                return !['spam', 'zero_clicks', 'pending', 'all_publishers'].includes(key) && !['spam', '0 click', 'pending'].includes(label);
            });
        }
        const pendingTab = currentTabs.find(t => t.label?.toLowerCase() === 'pending');
        const otherTabs = currentTabs.filter(t => t.label?.toLowerCase() !== 'pending');
        return pendingTab ? [...otherTabs, pendingTab] : currentTabs;
    }, [tabs, isClient]);
    const activeTab = sortedTabs.find(t => t.key === activeTabKey) ?? sortedTabs[0];
    const tabItems = useMemo(() => sortedTabs.map(t => ({ label: t.label, value: t.key })), [sortedTabs]);
    // ── renderHeader — identical pattern to PublisherReporting ────────────────
    const renderHeader = useCallback((column) => {
        const isSorted = sortBy === toSortKey(column.id);
        const direction = isSorted ? sortOrder : 'desc';
        return (_jsxs(Box, { sx: {
                display: 'flex', alignItems: 'center', gap: 0.5, width: '100%',
                cursor: column.sortable ? 'pointer' : 'default'
            }, onClick: (e) => {
                if (!column.sortable)
                    return;
                e.stopPropagation();
                onSortChange(toSortKey(column.id), isSorted ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc');
            }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '12px !important', fontWeight: 700, textTransform: 'capitalize', color: '#252b37' }, children: column.label }), column.sortable && (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', opacity: isSorted ? 1 : 0.2 }, children: direction === 'desc' ? (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M2.667 11.333h4M2.667 8h6M12 7.333v5.334m0 0 2-2m-2 2-2-2m-7.333-6h8", stroke: "#181d27", strokeLinecap: "round", strokeLinejoin: "round" }) })) : (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M2.667 11.333h8M2.667 8h6m-6-3.333h4m5.333 4V3.333m0 0 2 2m-2-2-2 2", stroke: "#181d27", strokeLinecap: "round", strokeLinejoin: "round" }) })) })), column.tooltip && (_jsx(InfoTooltip, { title: column.tooltip, placement: "top" }))] }));
    }, [sortBy, sortOrder, onSortChange, toSortKey]);
    // ── Columns — dynamically built from tab headers ─────────────────────────
    const columns = useMemo(() => {
        const headerMap = {
            partner_name: { label: 'Partner Name', width: '200px', sortable: false },
            revenue: { label: 'Revenue', width: '140px', sortable: true },
            roas: { label: 'ROAS', width: '110px', sortable: true },
            clicks: {
                label: 'Clicks', width: '120px', sortable: true,
                tooltip: 'Total clicks tracked for this publisher in the selected date range'
            },
            orders: { label: 'Orders', width: '110px', sortable: true },
            spend: { label: 'Spend', width: '130px', sortable: true },
            tof: { label: 'New Customer', width: '170px', sortable: true },
            mof: { label: 'Engaged Customer', width: '190px', sortable: true },
            bof: { label: 'Existing Customer', width: '190px', sortable: true },
            tag: { label: 'Tag', width: '120px', sortable: false },
        };
        // If tab has headers field, use it to determine columns
        let headerIds = activeTab?.headers && activeTab.headers.length > 0
            ? activeTab.headers
            : ['partner_name', 'revenue', 'roas', 'clicks', 'orders', 'spend']; // fallback
        if (!canManageTagging) {
            headerIds = headerIds.filter(id => id !== 'tag');
        }
        return headerIds.map(id => ({
            id,
            label: (isOrdersView && id === 'revenue') ? 'Orders' : (headerMap[id]?.label || id),
            width: headerMap[id]?.width || '120px',
            align: 'left',
            sortable: headerMap[id]?.sortable ?? false,
            renderHeader,
            tooltip: headerMap[id]?.tooltip,
        }));
    }, [activeTab?.headers, renderHeader, canManageTagging, isOrdersView]);
    // ── Cell renderer ─────────────────────────────────────────────────────────
    const renderCell = useCallback((row, column) => {
        if (isOrdersView && ['revenue', 'tof', 'mof', 'bof'].includes(column.id)) {
            const isTotal = column.id === 'revenue';
            const metric = isTotal ? row.orders : row[`${column.id}_orders`];
            if (!metric)
                return _jsx(Typography, { sx: { color: '#667085' }, children: "\u2014" });
            const totalOrders = row.orders?.current ?? 0;
            return (_jsx(MetricCell, { metric: metric, format: "number", contributionPct: isTotal ? undefined : (totalOrders > 0 ? (metric.current / totalOrders) * 100 : 0) }));
        }
        switch (column.id) {
            case 'partner_name': {
                const networkInfo = NETWORK_METADATA_MAP[row.network?.toLowerCase()];
                const displayName = cleanPartnerName(row.partner_name);
                // CJ action badge: both trackers = ALL, otherwise RP / WP
                const hasRetail = row.action_settings?.includes('retail_purchase');
                const hasWholesale = row.action_settings?.includes('wholesale_purchase');
                const actionBadge = hasRetail && hasWholesale
                    ? { label: 'ALL', tooltip: 'Retail Purchase + Wholesale Purchase' }
                    : hasRetail
                        ? { label: 'RP', tooltip: 'Retail Purchase' }
                        : hasWholesale
                            ? { label: 'WP', tooltip: 'Wholesale Purchase' }
                            : null;
                return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(HoverTooltip, { title: displayName, placement: "top", arrow: true, children: _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                    fontSize: '14px !important',
                                    color: '#101828',
                                    fontWeight: 400,
                                    maxWidth: '180px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'default'
                                }, children: displayName }) }), networkInfo?.icon ? (_jsx(HoverTooltip, { title: networkInfo.name, placement: "top", arrow: true, children: _jsx("img", { src: networkInfo.icon, alt: row.network, style: { width: 14, height: 14, objectFit: 'contain', flexShrink: 0 } }) })) : (_jsx(Typography, { sx: {
                                fontSize: '10px', color: '#667085', bgcolor: '#F2F4F7',
                                px: '6px', py: '1px', borderRadius: '4px', fontWeight: 500,
                                textTransform: 'uppercase'
                            }, children: row.network })), actionBadge && (_jsx(HoverTooltip, { title: actionBadge.tooltip, placement: "top", arrow: true, children: _jsx(Typography, { sx: {
                                    fontSize: '10px', color: '#667085', bgcolor: '#F2F4F7',
                                    px: '6px', py: '1px', borderRadius: '4px', fontWeight: 500,
                                    ml: '-4px', flexShrink: 0, cursor: 'default'
                                }, children: actionBadge.label }) }))] }));
            }
            case 'revenue': return row.revenue ? _jsx(MetricCell, { metric: row.revenue, format: "currency", prefix: "$" }) : null;
            case 'roas': return row.roas ? _jsx(MetricCell, { metric: row.roas, format: "decimal" }) : null;
            case 'clicks': return row.clicks ? _jsx(MetricCell, { metric: row.clicks, format: "number" }) : null;
            case 'orders': {
                if (!row.orders)
                    return null;
                const net = (row.network || '').toLowerCase().trim();
                const isUnsupported = net === 'levanta' || net === 'awin' || net === 'refersion' || net === 'costingtons' || net === 'wayward';
                return (_jsx(Box, { onClick: () => handleOrdersClick(row), sx: {
                        cursor: isUnsupported ? 'default' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                    }, children: _jsx(MetricCell, { metric: row.orders, format: "number", hasDottedUnderline: !isUnsupported }) }));
            }
            case 'spend': return row.spend ? _jsx(MetricCell, { metric: row.spend, format: "currency", prefix: "$" }) : null;
            case 'tof': return row.tof ? _jsx(MetricCell, { metric: row.tof, format: "currency", prefix: "$", contributionPct: row.revenue?.current > 0 ? (row.tof.current / row.revenue.current) * 100 : 0 }) : _jsx(Typography, { sx: { color: '#667085' }, children: "\u2014" });
            case 'mof': return row.mof ? _jsx(MetricCell, { metric: row.mof, format: "currency", prefix: "$", contributionPct: row.revenue?.current > 0 ? (row.mof.current / row.revenue.current) * 100 : 0 }) : _jsx(Typography, { sx: { color: '#667085' }, children: "\u2014" });
            case 'bof': return row.bof ? _jsx(MetricCell, { metric: row.bof, format: "currency", prefix: "$", contributionPct: row.revenue?.current > 0 ? (row.bof.current / row.revenue.current) * 100 : 0 }) : _jsx(Typography, { sx: { color: '#667085' }, children: "\u2014" });
            case 'tag': return (_jsx(InlineTagDropdown, { tag: row.tag, onSave: (t) => handleSaveTag(row.id || row.publisher_id, t), saving: updating, disabled: !canManageTagging }));
            default: return null;
        }
    }, [canManageTagging, handleSaveTag, updating, isOrdersView]);
    if (!activeTab)
        return null;
    const { rows, pagination, metrics } = activeTab;
    // Client-side search filter on partner_name
    const filteredRows = useMemo(() => {
        if (!searchVal.trim())
            return rows;
        const q = searchVal.toLowerCase();
        return rows.filter(r => r.partner_name?.toLowerCase().includes(q) || r.network?.toLowerCase().includes(q));
    }, [rows, searchVal]);
    return (_jsxs(Box, { sx: { bgcolor: '#fff', borderRadius: '8px', border: '1px solid #E5E7EB', overflow: 'hidden' }, children: [_jsxs(Box, { className: "publisher-reporting-toolbar", sx: {
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1, py: 0, bgcolor: '#fff', width: '100%',
                }, children: [_jsx(CommonTabs, { selectedValue: activeTabKey, onChange: onTabChange, tabs: tabItems }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '12px' }, children: [activeTabKey === 'all' && (_jsx(Box, { sx: {
                                    display: 'inline-flex', p: '2px', gap: '2px',
                                    border: '1px solid #E5E7EB', borderRadius: '8px', bgcolor: '#F9FAFB',
                                }, children: ['revenue', 'orders'].map(view => {
                                    const selected = allView === view;
                                    return (_jsx(Box, { role: "button", onClick: () => handleViewChange(view), sx: {
                                            px: '12px', py: '4px', borderRadius: '6px', cursor: 'pointer',
                                            fontSize: '13px', fontWeight: 500, textTransform: 'capitalize',
                                            bgcolor: selected ? '#fff' : 'transparent',
                                            color: selected ? '#101828' : '#667085',
                                            boxShadow: selected ? '0 1px 2px rgba(16,24,40,0.1)' : 'none',
                                        }, children: view }, view));
                                }) })), _jsx(TableToolbar, { title: "", showSearch: true, showExpand: true, showFullScreen: true, searchValue: searchVal, onSearchChange: setSearchVal, searchPlaceholder: "Search publishers..." })] })] }), _jsx(Box, { sx: { minHeight: '408px' }, children: _jsx(CommonTable, { columns: columns, rows: filteredRows, renderCell: renderCell, isLoading: loading, isFirstLoad: loading && rows.length === 0, enableSummaryRow: activeTabKey !== 'zero_clicks' && filteredRows.length > 0, renderSummaryRow: isOrdersView ? buildOrdersSummaryRenderer(metrics, hasCompare) : buildSummaryRenderer(metrics, hasCompare), summaryRowPosition: "bottom", sortConfig: { columnId: isOrdersView ? (ORDERS_SORT_KEY_REVERSE[sortBy] ?? sortBy) : sortBy, direction: sortOrder }, onSortChange: (colId, dir) => onSortChange(toSortKey(colId), dir || 'desc'), expandTrigger: "none", stickyHeader: true, maxHeight: "408px" }) }), filteredRows.length > 0 && (_jsx(Box, { sx: { borderTop: '1px solid #E5E7EB' }, children: _jsx(Pagination, { currentPage: pagination.page, totalPages: pagination.total_pages, totalEntries: pagination.total, itemsPerPage: pagination.limit, onPageChange: (page) => onPageChange(activeTabKey, page), showEntriesMessage: true }) })), _jsx(ReferralUrlsModal, { open: referralModalOpen, onClose: () => setReferralModalOpen(false), publisher: selectedPublisherForReferrals, clientId: selectedClientId, startDate: dateRange?.start || '', endDate: dateRange?.end || '' })] }));
};
export default PublisherTable;
